import { createContext, useContext, useState, useEffect } from 'react';
import { apiClient, API_ENDPOINTS } from '../config/api';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.ADMIN.AUTH.ME, {
        timeout: 60000, // 60 seconds - allows time for Render service to wake up
      });
      if (response.data.success) {
        setAdmin(response.data.admin);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        setAdmin(null);
      }
    } catch (error) {
      // 401 is expected when not logged in - don't treat it as an error
      // Only log other errors for debugging
      if (error.response?.status === 401) {
        // User is not authenticated - this is normal
        setIsAuthenticated(false);
        setAdmin(null);
      } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        // Timeout - server might be sleeping (Render free tier)
        // Don't log as error, just silently fail - user can try again
        console.warn('Auth check timeout - server may be waking up (Render free tier can take 30-60s)');
        setIsAuthenticated(false);
        setAdmin(null);
      } else {
        // Log other errors (network, server errors, etc.)
        console.error('Auth check error:', error.response?.status || error.message);
        setIsAuthenticated(false);
        setAdmin(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      // Render.com free tier services can take 30-60 seconds to wake up from sleep
      // The axios instance already has a 60s timeout, so we don't need a separate timeout here
      const response = await apiClient.post(
        API_ENDPOINTS.ADMIN.AUTH.LOGIN,
        { username, password },
        {
          timeout: 60000, // 60 seconds - allows time for Render service to wake up
        }
      );
      
      if (response.data.success) {
        setAdmin(response.data.admin);
        setIsAuthenticated(true);
        // Return success - let the component handle navigation
        // Don't use window.location.href here as it prevents setLoading(false) from running
        return { 
          success: true,
          redirectPath: sessionStorage.getItem('redirectAfterLogin') || '/admin/dashboard'
        };
      }
      return { success: false, message: response.data.message };
    } catch (error) {
      // Log error for debugging
      console.error('Login error:', error);
      
      // Handle different error types
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        return {
          success: false,
          message: 'Request timed out. The server may be waking up (this can take up to 60 seconds on free hosting). Please wait a moment and try again.'
        };
      }
      
      if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
        return {
          success: false,
          message: 'Network error. Please check your connection and try again. If the server is sleeping, it may take up to 60 seconds to respond.'
        };
      }
      
      if (error.response?.status === 503 || error.response?.status === 502) {
        return {
          success: false,
          message: 'Server is temporarily unavailable. The service may be waking up. Please wait a moment and try again.'
        };
      }
      
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Login failed. Please try again.'
      };
    }
  };

  const logout = async () => {
    try {
      await apiClient.post(API_ENDPOINTS.ADMIN.AUTH.LOGOUT, {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setAdmin(null);
      setIsAuthenticated(false);
      // Redirect to login page
      window.location.href = '/admin/login';
    }
  };

  return (
    <AdminAuthContext.Provider value={{ admin, isAuthenticated, loading, login, logout, checkAuth }}>
      {children}
    </AdminAuthContext.Provider>
  );
};


